import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const CATS = ["Mechanical", "Electrical", "Hydraulic", "Civil", "Consumables", "Service Provider", "Transporter", "OEM", "Laboratory"];

export default function Vendors() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const empty = { name: "", category: "Service Provider", contact_person: "", mobile: "", email: "", gst: "", pan: "", cin: "",
                  address: "", city: "", state: "", pin: "", country: "India", status: "active", rating: 0,
                  payment_terms: "Net 30", credit_days: 30, amc_details: "",
                  bank_name: "", bank_branch: "", bank_account: "", bank_ifsc: "", bank_swift: "", upi: "" };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/vendors").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try { await api.post("/vendors", form); toast.success("Vendor added"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = list.filter((v) => !q || `${v.name} ${v.code} ${v.category} ${v.gst || ""} ${v.city || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout title="Vendors" subtitle="Supplier master with GST, banking, AMC and document linkage">
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="Add Vendor"
        addTestId="vendor-add-button" searchTestId="vendor-search-input" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
            <TableHead>Contact</TableHead><TableHead>City</TableHead><TableHead>GST</TableHead>
            <TableHead>Bank</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Rating</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filt.map((v) => (
              <TableRow key={v.id} data-testid={`vendor-row-${v.code}`}>
                <TableCell className="font-mono font-semibold">{v.code}</TableCell>
                <TableCell><div className="font-medium">{v.name}</div><div className="text-xs text-slate-400">{v.email}</div></TableCell>
                <TableCell><StatusBadge status="active" /> <span className="ml-1 text-xs">{v.category}</span></TableCell>
                <TableCell className="text-sm"><div>{v.contact_person}</div><div className="text-xs font-mono text-slate-500">{v.mobile}</div></TableCell>
                <TableCell className="text-xs">{v.city}{v.state ? `, ${v.state}` : ""}</TableCell>
                <TableCell className="font-mono text-xs">{v.gst || "—"}</TableCell>
                <TableCell className="text-xs"><div>{v.bank_name || "—"}</div><div className="text-slate-400 font-mono">{v.bank_ifsc}</div></TableCell>
                <TableCell><StatusBadge status={v.status} /></TableCell>
                <TableCell className="text-right font-mono">{v.rating || 0}/5</TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">No vendors</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vendor-create-dialog">
          <DialogHeader><DialogTitle>Add Vendor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2 md:col-span-3"><div className="label-cap text-slate-500 mb-1">Identity</div></div>
            <div><Label className="label-cap">Name *</Label><Input data-testid="vendor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="vendor-category-input"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-cap">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="vendor-status-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["active", "inactive"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[["contact_person","Contact Person"],["mobile","Mobile"],["email","Email"],
              ["gst","GST"],["pan","PAN"],["cin","CIN"],
              ["address","Address"],["city","City"],["state","State"],
              ["pin","PIN"],["payment_terms","Payment Terms"],["credit_days","Credit Days"]].map(([k, l]) => (
              <div key={k}><Label className="label-cap">{l}</Label>
                <Input data-testid={`vendor-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: k === "credit_days" ? parseInt(e.target.value) || 0 : e.target.value })} />
              </div>
            ))}
            <div className="col-span-2 md:col-span-3 mt-2"><div className="label-cap text-slate-500 mb-1">Banking</div></div>
            {[["bank_name","Bank Name"],["bank_branch","Branch"],["bank_account","Account #"],
              ["bank_ifsc","IFSC"],["bank_swift","SWIFT"],["upi","UPI ID"]].map(([k, l]) => (
              <div key={k}><Label className="label-cap">{l}</Label>
                <Input data-testid={`vendor-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="vendor-submit-button">Save Vendor</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
